import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useLocationParams } from 'hooks';
import { getCampaigns } from '../api';
import queryKeys from '../api/queryKeys';
import { buildCampaignFetchParams } from '../utils/campaignFetchParams';
import { restoreSelectedCampaignsFromUrl } from '../utils/campaignUrlSync';
import { isDeepLinkStepPass, resolveFetchedStepStatus, } from '../utils/deepLinkInit';
import type { DeepLinkDependentStepGate, DeepLinkStepResult, } from './deepLinkTypes';

const useDeepLinkCampaignsStep = ({
  enabled,
  previousPass,
}: DeepLinkDependentStepGate): DeepLinkStepResult => {
  const { setValue } = useFormContext();
  const {
    marketingProcess: marketingProcessParam,
    adAccounts: adAccountsParam,
    selectedCampaigns: selectedCampaignsParam,
  } = useLocationParams();

  const urlCampaignIds = useMemo(
    () => [...new Set(selectedCampaignsParam.map((campaign) => campaign.id))],
    [selectedCampaignsParam],
  );

  const campaignRequestParams = useMemo(
    () =>
      buildCampaignFetchParams(
        marketingProcessParam ?? undefined,
        [...adAccountsParam],
        urlCampaignIds,
      ),
    [adAccountsParam, marketingProcessParam, urlCampaignIds],
  );

  // Only the targeted ids request runs here — validating the URL campaigns
  // doesn't need the full browse list, which the Campaigns step fetches (and
  // merges these ids into) once the user actually opens it.
  const campaignsQuery = useQuery({
    staleTime: 30_000,
    queryFn: ({ signal }) =>
      getCampaigns({ params: campaignRequestParams }, signal),
    queryKey: [
      ...queryKeys.campaigns,
      'by-ids',
      campaignRequestParams.facebookAccountId,
      campaignRequestParams.campaignNameFilter,
      campaignRequestParams.status,
      urlCampaignIds,
    ],
    enabled:
      enabled &&
      previousPass &&
      adAccountsParam.length > 0 &&
      urlCampaignIds.length > 0,
  });

  const missingEntries = useMemo(
    () =>
      campaignsQuery.data
        ? selectedCampaignsParam.filter(
            (campaign) =>
              !campaignsQuery.data!.some(
                (row) => String(row.id) === campaign.id
              )
          )
        : [],
    [campaignsQuery.data, selectedCampaignsParam]
  );

  const missing = useMemo(
    () => missingEntries.map((campaign) => campaign.id),
    [missingEntries]
  );

  const status = resolveFetchedStepStatus({
    enabled,
    previousPass,
    emptySuccess: urlCampaignIds.length === 0,
    isPending: campaignsQuery.isPending,
    isError: campaignsQuery.isError,
    hasMissing: missingEntries.length > 0,
  });

  const restore = useCallback(() => {
    if (!enabled || !campaignsQuery.data) return;

    const { selectedCampaigns, selectedCampaignsBudgets } =
      restoreSelectedCampaignsFromUrl(
        selectedCampaignsParam,
        campaignsQuery.data
      );
    setValue('selectedCampaigns', selectedCampaigns);
    setValue('selectedCampaignsBudgets', selectedCampaignsBudgets);
  }, [campaignsQuery.data, enabled, selectedCampaignsParam, setValue]);

  return {
    status,
    pass: isDeepLinkStepPass(enabled, status),
    missing,
    restore,
  };
};

export default useDeepLinkCampaignsStep;
